import { compareCzech } from "./czechSort";

export * from "./czechSort";

// ============================================================================
// PRICE FORMATTING
// ============================================================================

const NUMBER_FORMAT_CACHE = new Map<string, Intl.NumberFormat>();

/**
 * Memoizovaný `Intl.NumberFormat` — drahá je v `Intl` SAZBA formátovače, ne `.format()`.
 * Změřeno na tomhle repu (200 000 volání): `new Intl.NumberFormat(…).format(n)` stojí
 * 15,96 µs/volání, přes tuhle cache 0,48 µs (compact 19,43 µs → 0,68 µs), tedy ~30×.
 *
 * Není to mikrooptimalizace: `formatPrice`/`formatMoney` se volají uvnitř `.map()` řádků
 * tabulek a v Recharts `formatter` callbacích, tedy JEDNOU NA DATOVÝ BOD při každém
 * renderu i hoveru — analytická obrazovka s ~200 částkami spadne z 3,2 ms na 0,1 ms.
 * Nově se volá i na serveru (portál, newsletter), kde se to platí v CPU time Workeru.
 *
 * Klíč je `locale` + `JSON.stringify(options)`. Stringify stojí ~0,2 µs (proto 0,48 a ne
 * 0,27 µs holého `Map.get`) a je to VĚDOMÁ cena za klíč, který nejde zkazit: ručně
 * skládaný klíč z vyjmenovaných voleb by u nově přidané volby tiše vrátil formátovač
 * s jiným formátem. Cache je modulová a neomezená — kombinací je hrstka
 * (jazyky × měny × notace), ne uživatelský vstup.
 */
export function numberFormatter(
  locale: string,
  options: Intl.NumberFormatOptions = {},
): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`;
  let formatter = NUMBER_FORMAT_CACHE.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options);
    NUMBER_FORMAT_CACHE.set(key, formatter);
  }
  return formatter;
}

export function formatPrice(
  price: number,
  currency: string = "CZK",
  locale: string = "cs-CZ",
): string {
  return numberFormatter(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

/**
 * Canonical money formatter for documents/invoices/portal/PDF — "12 345 Kč" / "1 234 €" with
 * cs-CZ thin-space grouping and a currency suffix (CZK→Kč, EUR→€, else the raw code). The app
 * stores money as WHOLE units of its currency (not minor units), so no division. Single source
 * of truth so a contract PDF and its invoice can never disagree on formatting. NOTE: qrPayment's
 * SPAYD/EPC amount (toFixed(2)) is a machine payload, NOT display — keep it separate.
 */
/**
 * JAK se měna píše — jediná definice pro celý systém. `Intl` se `style: "currency"` sem
 * nesmí: `cs-CZ` z něj tiskne „US$" a „1 200 US$" ve smlouvě vedle „1 200 USD" ve faktuře
 * je rozpor v jednom dokladu. Neznámý kód se píše doslova (ISO), ne symbolem.
 */
export function currencySuffix(currency: string): string {
  const cur = (currency || "CZK").toUpperCase();
  return cur === "CZK" ? "Kč" : cur === "EUR" ? "€" : cur;
}

export function formatMoney(
  amount: number,
  currency: string = "CZK",
  locale: string = "cs-CZ",
): string {
  const n = numberFormatter(locale, { maximumFractionDigits: 0 }).format(amount || 0);
  return `${n} ${currencySuffix(currency)}`;
}

export function formatPriceCompact(price: number): string {
  if (price >= 1000000) {
    return `${(price / 1000000).toFixed(1)} mil.`;
  }
  if (price >= 1000) {
    return `${Math.round(price / 1000)} tis.`;
  }
  return price.toString();
}

/** Náhrada ceny na veřejném webu, když zájezd cenu „od" nemá. */
export const PRICE_ON_REQUEST_CZ = "Cena na vyžádání";
/** Krátká varianta pro místa, kam se dlouhý text nevejde (bublina ceny v mapě). */
export const PRICE_ON_REQUEST_SHORT_CZ = "Na dotaz";

/**
 * Má zájezd/hotel veřejně zobrazitelnou cenu „od"?
 *
 * JEDINÁ definice pro celý web: `priceFrom` je v DB nullable a část katalogu ho má
 * 0 (zájezd na míru, rozpracovaná kalkulace). `formatPrice(0)` vrátí „0 Kč", což
 * zákazník čte jako „zdarma" — proto se nulová a chybějící cena musí všude chovat
 * stejně jako žádná cena. Type guard, takže volající pak pracuje s `number`.
 */
export function hasPublicPrice(price: number | null | undefined): price is number {
  return typeof price === "number" && Number.isFinite(price) && price > 0;
}

/** Cena „od" pro veřejný web: „12 345 Kč", nebo „Cena na vyžádání" místo „0 Kč". */
export function formatPublicPrice(price: number | null | undefined): string {
  return hasPublicPrice(price) ? formatPrice(price) : PRICE_ON_REQUEST_CZ;
}

/**
 * Varianta s předponou pro řádky typu „7 dní · od 32 900 Kč". Bez ceny vrací
 * `PRICE_ON_REQUEST_CZ` BEZ „od" — „od Cena na vyžádání" by byl nesmysl.
 */
export function formatPublicPriceFrom(price: number | null | undefined): string {
  return hasPublicPrice(price) ? `od ${formatPrice(price)}` : PRICE_ON_REQUEST_CZ;
}

// ============================================================================
// DATE FORMATTING
// ============================================================================

export function formatDate(
  date: Date | string,
  options?: Intl.DateTimeFormatOptions,
  locale: string = "en-US",
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString(locale, {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    ...options,
  });
}

/**
 * České datum pro web. Prázdný/neplatný vstup dává prázdný řetězec — karta pak řádek
 * s datem prostě nevykreslí místo toho, aby zobrazila „Invalid Date". Existuje proto,
 * že `toLocaleDateString("cs-CZ", …)` s tím samým guardem byl rozepsaný v pěti
 * komponentách webu, pokaždé o kousek jinak.
 */
export function formatCzechDate(
  date: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = { day: "numeric", month: "numeric", year: "numeric" },
): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("cs-CZ", options);
}

export function formatDateRange(
  startDate: Date | string,
  endDate: Date | string,
  locale: string = "cs-CZ",
): string {
  const start = typeof startDate === "string" ? new Date(startDate) : startDate;
  const end = typeof endDate === "string" ? new Date(endDate) : endDate;

  const startStr = start.toLocaleDateString(locale, {
    day: "numeric",
    month: "numeric",
  });
  const endStr = end.toLocaleDateString(locale, {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });

  return `${startStr} - ${endStr}`;
}

export function formatRelativeDate(date: Date | string, locale: string = "cs"): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "dnes";
  if (diffDays === 1) return "zítra";
  if (diffDays === -1) return "včera";
  if (diffDays > 0 && diffDays <= 7)
    return `za ${diffDays} ${pluralize(diffDays, "den", "dny", "dní")}`;
  if (diffDays < 0 && diffDays >= -7)
    return `před ${Math.abs(diffDays)} ${pluralize(Math.abs(diffDays), "dnem", "dny", "dny")}`;

  return formatDate(d, undefined, locale);
}

const MS_PER_DAY = 86_400_000;

/**
 * ISO den → UTC půlnoc. Ořez na prvních 10 znaků je záměrný: vstup může být
 * `YYYY-MM-DD` i celý datetime a v obou případech musí vyjít TÝŽ den, bez ohledu
 * na časovou zónu běhu (Workers běží v UTC, prohlížeč v Praze).
 */
function isoDayStartUtc(iso: string): number {
  return Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
}

/**
 * Je vstup KALENDÁŘNĚ platné `YYYY-MM-DD`? Přísně: zero-padded a existující den,
 * takže `2026-8-1`, `2026-13-45` ani `abc` neprojdou.
 *
 * Existuje jako VSTUPNÍ BRÁNA před `shiftIsoDate`/`isoDayDiff` — obě staví `Date`
 * a nad nesmyslným vstupem by `toISOString()` hodilo `RangeError: Invalid time value`
 * (tj. 500 z veřejného endpointu). Kdo bere datum zvenčí (query string, formulář,
 * import), musí ho prohnat tudy a nesmysl zahodit, ne poslat dál.
 */
export function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const ms = isoDayStartUtc(value);
  // Zpětný převod, ne jen `!Number.isNaN`: V8 přetečený den TICHE přepočítá
  // (`2026-02-31T00:00:00Z` → 3. března), takže platnost pozná jen shoda s originálem.
  return !Number.isNaN(ms) && new Date(ms).toISOString().slice(0, 10) === value;
}

/**
 * Posun ISO data o N dní (kladně i záporně), výsledek zase `YYYY-MM-DD`.
 *
 * KANONICKÝ domov téhle aritmetiky (řez 02). Počítá se v UTC, takže přechod na letní čas
 * nikdy neukousne ani nepřidá den.
 *
 * Zmigrováno: `entryPortals/kh.ts`, `invoiceService.ts`, admin `newsletter/QueuePanel.tsx`.
 * DOSUD NE (a je to práce k dodělání, ne stav věcí): `apps/api/src/lib/backSchedule.ts`
 * (`minusDays`) a `apps/api/src/lib/entryTasks.ts` — obě jsou `-days` varianty téhož těla
 * a projdou sem jako `shiftIsoDate(iso, -days)`.
 *
 * POZOR: vstup musí být platné ISO datum (`isIsoDate`) — jinak vznikne `Invalid Date`
 * a `toISOString()` vyhodí `RangeError`. Validuje se na hranici (parser query stringu),
 * ne tady: tichá degradace uvnitř aritmetiky by schovala vadné datum ve faktuře.
 */
export function shiftIsoDate(iso: string, days: number): string {
  const d = new Date(isoDayStartUtc(iso));
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Rozdíl dvou ISO dat ve dnech; kladné číslo = `to` je později než `from`. */
export function isoDayDiff(from: string, to: string): number {
  return Math.round((isoDayStartUtc(to) - isoDayStartUtc(from)) / MS_PER_DAY);
}

export function daysUntil(date: Date | string): number {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

// ============================================================================
// STRING UTILITIES
// ============================================================================

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-"); // Remove consecutive hyphens
}

/**
 * Normalize text for search matching (vize svetovy-katalog A1): lowercase,
 * strip diacritics (NFD + combining marks), trim and collapse whitespace.
 * MUST be applied to BOTH sides (indexed content and the query) — see
 * apps/api searchSuggest and the web search inputs.
 */
export function normalizeSearchText(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Derive a fallback `alt` text from a filename when AI alt-suggest fails.
 * Strips the extension, replaces underscores/dashes/dots/brackets with spaces,
 * and collapses runs of whitespace — so screen readers don't read out
 * `vietnam_trip.v2.heic` as "vietnam underscore trip dot v two dot heic".
 * Shared between the MediaPicker (admin) and the upload route (api).
 */
export function altFromFilename(filename: string): string {
  return (
    filename
      .replace(/\.[^.]+$/, "")
      .replace(/[._\-()[\]{}]+/g, " ")
      .replace(/\s+/g, " ")
      .trim() || "image"
  );
}

export function truncate(text: string, maxLength: number = 100): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).replace(/\s+\S*$/, "") + "...";
}

/**
 * Znak → XML entita. JEDINÝ escape pro XML, které aplikace skládá ručně (sitemapy).
 *
 * Není to kosmetika: slug je v adminu pole prostého textu a žádné z jeho schémat
 * znakovou třídu nevynucuje, takže stačí jediné `sapa-&-bac-ha` a dokument přestane
 * být well-formed — parser pak odmítne CELOU sitemapu, ne jeden záznam. Escapuje se
 * všech pět entit z protokolu sitemap.org, takže funkce platí pro obsah elementu
 * i pro hodnotu atributu.
 */
export function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/** HTML entity, které se do holého textu překládají doslova (ostatní se nechávají být). */
const NAMED_ENTITIES: Record<string, string> = {
  "&nbsp;": " ",
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&apos;": "'",
  "&hellip;": "…",
  "&ndash;": "–",
  "&mdash;": "—",
};

/**
 * Číselná entita → znak, ale jen pro platný kódový bod.
 *
 * `String.fromCodePoint` VYHAZUJE `RangeError` pro cokoli mimo `0…0x10FFFF` (a pro
 * neceločíselné vstupy), takže `&#1114112;` nebo `&#x110000;` z admin-psaného Tiptapu by
 * shodily celý `toPlainText`. Ten je přes `seoMeta.ts` na SSR cestě KAŽDÉ detailové
 * stránky, kde by neodchycená výjimka v Astro frontmatteru vrátila 500 místo stránky.
 *
 * Neplatná entita se proto nechává doslova — stejné pravidlo jako u neznámé pojmenované
 * entity (`&euro;`): raději viditelný artefakt v textu než smazaný obsah nebo pád.
 * Náhradní pár (`0xD800…0xDFFF`) je taky „neplatný": `fromCodePoint` ho sice přijme, ale
 * vyrobí osamocený surrogate, tedy nevalidní UTF-16 putující dál do `<meta>` a JSON-LD.
 */
const MAX_CODE_POINT = 0x10ffff;
function decodeNumericEntity(raw: string, radix: number, entity: string): string {
  const code = radix === 16 ? parseInt(raw, 16) : Number(raw);
  if (!Number.isInteger(code) || code < 0 || code > MAX_CODE_POINT) return entity;
  if (code >= 0xd800 && code <= 0xdfff) return entity;
  return String.fromCodePoint(code);
}

/**
 * HTML (rich-text z Tiptapu) → holý jednořádkový text.
 *
 * KANONICKÝ domov strip-HTML: totéž se v aplikaci psalo PĚTKRÁT, pokaždé chudší —
 * `apps/api/src/lib/contentEdit.ts`, `gmailSync.ts`, `agent/context.ts` a
 * `newsletter/render.ts` značky jen mažou, takže jim `<script>alert(1)</script>` nechá
 * v „textu" tělo skriptu a `&amp;` zůstane doslova. Tahle (webová) varianta navíc
 * vyhazuje CELÝ obsah `<script>`/`<style>` a dekóduje entity. Zbylé čtyři sourozence
 * na ni ZMIGROVAT — každý zvlášť, ať se dá u každého ověřit, že se výstup nezhoršil.
 *
 * Neznámá entita se NEMAŽE (zůstane `&euro;`): vymazat ji za mezeru by z ceny udělalo
 * „100 za den". Číselná entita mimo rozsah Unicode se ze stejného důvodu nechává taky —
 * viz `decodeNumericEntity`, kde by jinak `RangeError` shodil SSR celé stránky.
 */
export function toPlainText(value: string | null | undefined): string {
  if (!value) return "";
  return value
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&#(\d+);/g, (entity, code: string) => decodeNumericEntity(code, 10, entity))
    .replace(/&#x([0-9a-f]+);/gi, (entity, code: string) => decodeNumericEntity(code, 16, entity))
    .replace(/&[a-z]+;/gi, (entity) => NAMED_ENTITIES[entity.toLowerCase()] ?? entity)
    .replace(/\s+/g, " ")
    .trim();
}

export function pluralize(count: number, singular: string, few: string, many: string): string {
  if (count === 1) return singular;
  if (count >= 2 && count <= 4) return few;
  return many;
}

export function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Customer name as published with a review: the first given name and the surname initial
 * with a dot („Jana N."). The single place of the rule (owner decision 21. 9. 2026 — data
 * minimisation): the full name is never stored on a customer review.
 *
 * - Given name = first word of `firstName` after leading academic titles (words ending with
 *   a dot); further given names are dropped; case and diacritics are kept as typed.
 *   Both names are composed to NFC first, so a decomposed „C + ˇ" (paste, import) keeps
 *   its háček in the initial.
 * - Initial = first letter anywhere in `lastName` (non-letters are skipped), upper-cased,
 *   Czech digraph „ch" → „Ch.". A surname without any letter adds nothing.
 * - No given name → `null` (the caller substitutes its anonymous convention): a lone
 *   initial is not a name and the full surname is more than the owner wants published.
 */
export function shortCustomerName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string | null {
  const given = (firstName ?? "")
    .normalize("NFC")
    .trim()
    .split(/\s+/)
    .find((word) => word !== "" && !word.endsWith("."));
  if (!given) return null;

  const surname = (lastName ?? "").normalize("NFC").trim();
  const at = surname.search(/\p{L}/u);
  if (at < 0) return given;
  const isDigraphCh = /^ch/i.test(surname.slice(at, at + 2));
  const initial = isDigraphCh
    ? "Ch"
    : String.fromCodePoint(surname.codePointAt(at) ?? 0).toLocaleUpperCase("cs");
  return `${given} ${initial}.`;
}

// ============================================================================
// TRIP UTILITIES
// ============================================================================

export function formatDuration(days: number): string {
  if (days === 1) return "1 den";
  if (days >= 2 && days <= 4) return `${days} dny`;
  return `${days} dní`;
}

/** Czech plural form: 1 → one, 2–4 → few, else → many. */
export function czechPlural(n: number, one: string, few: string, many: string): string {
  if (n === 1) return one;
  if (n >= 2 && n <= 4) return few;
  return many;
}

/**
 * Popisek cestujících česky: „2 dospělí, 1 dítě (5 let)“. Děti jen při `children > 0`,
 * věky jen s dětmi a neprázdným `childAges`. Jediný zdroj popisku pro web (nabídka,
 * hotelový widget), API (kontext AI konceptu poptávky) i e-mailové šablony.
 */
export function travellersLabel(
  adults: number,
  children?: number | null,
  childAges?: readonly number[] | null,
): string {
  const parts = [`${adults} ${czechPlural(adults, "dospělý", "dospělí", "dospělých")}`];
  const c = children ?? 0;
  if (c > 0) {
    const ages = childAges?.length
      ? ` (${childAges.map((a) => `${a} ${czechPlural(a, "rok", "roky", "let")}`).join(", ")})`
      : "";
    parts.push(`${c} ${czechPlural(c, "dítě", "děti", "dětí")}${ages}`);
  }
  return parts.join(", ");
}

/**
 * Format trip duration based on tripKind. Locale "cs" returns Czech (used in PDFs/emails);
 * "en" returns English (used on the public web).
 */
export function formatTripDuration(
  trip: {
    tripKind?: "tour" | "experience" | null;
    durationDays?: number | null;
    durationHours?: number | null;
  },
  locale: "cs" | "en" = "en",
): string {
  const isExperience = trip.tripKind === "experience";
  if (isExperience) {
    const h = trip.durationHours || 0;
    return locale === "cs" ? `${h} ${czechPlural(h, "hodina", "hodiny", "hodin")}` : `${h} hours`;
  }
  const d = trip.durationDays || 0;
  return locale === "cs" ? `${d} ${czechPlural(d, "den", "dny", "dní")}` : `${d} days`;
}

/**
 * Convert allowed weekdays (1..6,0) to disabled list for Calendar component.
 * If allowed is null/empty (= all days), returns empty disabled list.
 */
export function invertWeekdays(allowed: number[] | null | undefined): number[] {
  if (!allowed || allowed.length === 0) return [];
  return [0, 1, 2, 3, 4, 5, 6].filter((d) => !allowed.includes(d));
}

/** Compute earliest allowed booking date given lead time in hours from now. */
export function leadTimeMinDate(leadTimeHours: number): Date {
  return new Date(Date.now() + leadTimeHours * 3600_000);
}

/**
 * Hlášky kontroly dne odjezdu u zájezdu na vyžádání (řez 01). Jediný zdroj pro server
 * (`POST /api/inquiries`) i skript formuláře na webu, aby zákazník viděl tutéž větu, ať
 * den odmítne prohlížeč, nebo API.
 */
export const ON_REQUEST_DATE_MESSAGES = {
  missing: "Vyberte prosím termín.",
  invalid: "Zadejte prosím platné datum.",
  leadTime: (h: number) =>
    `Tento zájezd vyžaduje rezervaci alespoň ${h} h předem. Zvolte prosím pozdější termín.`,
  weekday: "Vybraný den není u tohoto zájezdu dostupný.",
} as const;

/**
 * The BOOKING axis (Pre-launch Area 3): does this trip book on an open calendar (`on_request`)
 * or from fixed departure dates? Reads `trips.fulfillmentMode`; falls back to
 * `tripKind === "experience"` for rows not yet backfilled. Use this for EVERY booking-axis
 * branch (calendar vs dropdown, pricing fallback, seat holds, inquiry validation) — never read
 * `tripKind` directly for booking, since multi-day TOURS can be on-request too.
 */
export function isOnRequestBooking(trip: {
  fulfillmentMode?: "fixed_departures" | "on_request" | null;
  tripKind?: "tour" | "experience" | null;
}): boolean {
  if (trip.fulfillmentMode) return trip.fulfillmentMode === "on_request";
  return trip.tripKind === "experience";
}

/**
 * Is this trip a HOTEL (vize svetovy-katalog D1)? A hotel is not its own entity: it is a
 * `resort` trip booked on an open calendar. Both halves matter — a resort with fixed
 * departures is a package holiday with departures, not a bookable hotel stay — so use this
 * helper for every hotel branch (admin tab hint, inquiry validation, /hotel presentation).
 */
export function isHotelTrip(trip: {
  tripType?: "guided" | "private" | "resort" | null;
  fulfillmentMode?: "fixed_departures" | "on_request" | null;
  tripKind?: "tour" | "experience" | null;
}): boolean {
  return trip.tripType === "resort" && isOnRequestBooking(trip);
}

export function calculateDiscount(originalPrice: number, currentPrice: number): number {
  if (!originalPrice || originalPrice <= currentPrice) return 0;
  return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
}

/**
 * Zůstatek míst termínu BEZ ořezu — pro provoz (admin editor termínů, průvodce
 * objednávkou). Přeprodaný odjezd tu vyjde záporně a provoz to má vidět.
 *
 * Tělo téhle funkce je JEDINÉ místo v produkčních zdrojích, kde se od kapacity odečítá
 * obsazenost (hlídá `scripts/kontrola/kapacita.mjs strom`, počet `odcitani`). Každá
 * další plocha čte buď ji, nebo zákaznickou `getAvailableSpots`.
 */
export function getSpotsBalance(capacity: number, booked: number): number {
  return capacity - booked;
}

/**
 * Volná místa termínu tak, jak je smí vidět ZÁKAZNÍK — nikdy záporná. Přeprodaný
 * odjezd je prostě vyprodaný (`isSoldOut` testuje `<= 0`).
 */
export function getAvailableSpots(capacity: number, booked: number): number {
  return Math.max(0, getSpotsBalance(capacity, booked));
}

/**
 * Derive a departure's occupancy level (Vlna 2 / Block 4.1, R1) from booked vs capacity —
 * the DERIVED axis that replaces the old stored `full`/`few_spots`/`open` status values.
 * Shared by web, admin and API so the threshold can never drift. `waitlist > 0` means the
 * departure is already over capacity, so it is full regardless of the booked/capacity ratio.
 */
export function occupancyLevel(
  booked: number,
  capacity: number,
  waitlist = 0,
): "open" | "few_spots" | "full" {
  if (waitlist > 0) return "full";
  const available = getAvailableSpots(capacity, booked);
  if (available <= 0) return "full";
  if (available <= 3) return "few_spots";
  return "open";
}

// ============================================================================
// ITINERARY UTILITIES
// ============================================================================

/**
 * Normalize meals from legacy object format {breakfast: true, dinner: true}
 * or new array format ["breakfast", "dinner"] to a consistent string[].
 */
export function normalizeMeals(meals?: string[] | Record<string, boolean | undefined>): string[] {
  if (!meals) return [];
  if (Array.isArray(meals)) return meals;
  return Object.entries(meals)
    .filter(([, v]) => v)
    .map(([k]) => k);
}

// ============================================================================
// ORDER UTILITIES
// ============================================================================

export function generateOrderNumber(prefix: string = "GO2"): string {
  const year = new Date().getFullYear();
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}-${year}-${random}`;
}

export function generateVoucherCode(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // Exclude confusing characters
  let code = "GO2-GIFT-";
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

// ============================================================================
// BLOG CONSTANTS
// ============================================================================

export const BLOG_CATEGORY_LABELS: Record<string, string> = {
  "travel-tips": "Travel Tips",
  culture: "Culture",
  food: "Food & Cuisine",
  nature: "Nature",
  guides: "Guides",
  news: "News",
};

// ============================================================================
// URL UTILITIES
// ============================================================================

/**
 * Id videa z jakéhokoli tvaru YouTube URL. `/live/` je součástí seznamu záměrně:
 * je to kanonická sdílecí URL streamované/premiérované epizody, tedy běžný tvar
 * podcastu — bez ní se uloží `videoId: null`, karta zůstane bez náhledu a noční
 * sync epizodu nespáruje, takže ji založí podruhé.
 */
// videa-vyjimka: YouTube část klasifikátoru, utils bez importů (packages/shared/src/videoLink.ts)
export function getYoutubeVideoId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

export function isYouTubeShort(url: string): boolean {
  return url.includes("/shorts/");
}

export function getYoutubeThumbnail(
  url: string,
  quality: "default" | "medium" | "high" | "maxres" = "high",
): string | null {
  const videoId = getYoutubeVideoId(url);
  if (!videoId) return null;

  const qualityMap = {
    default: "default",
    medium: "mqdefault",
    high: "hqdefault",
    maxres: "maxresdefault",
  };

  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
}

export function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, String(value));
    }
  }

  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export function parseQueryString(query: string): Record<string, string> {
  const params = new URLSearchParams(query);
  const result: Record<string, string> = {};

  for (const [key, value] of params) {
    result[key] = value;
  }

  return result;
}

// ============================================================================
// ATRIBUCE (UTM / click-id)
// ============================================================================

/**
 * Stropy hodnot, které sbíráme z ADRESNÍHO ŘÁDKU a ukládáme na lead. Bydlí tady, protože je
 * čtou DVĚ strany a musí souhlasit: poptávková schémata (`inquirySchema`,
 * `customInquirySchema`) jimi vstup validují a webový tracking jimi hodnotu OŘEZÁVÁ, než ji
 * k poptávce připojí.
 */
export const UTM_MAX_LENGTH = 255;
export const CLICK_ID_MAX_LENGTH = 512;

/**
 * Hodnota atribuce ořezaná na strop schématu; prázdná → `undefined` (pole se pak vůbec
 * neodesílá). Ořez patří na stranu ODESILATELE: `utm_campaign` bývá šablonovaná hodnota
 * z reklamní platformy a nikdo její délku negarantuje, kdežto poptávka je pro zákazníka
 * jediná cesta, jak se nám ozvat. Zamítnout kvůli dlouhému UTM celé odeslání (400 s anglickou
 * Zod hláškou, kterou zákazník nemá jak opravit) by znamenalo ztratit lead kvůli údaji, který
 * je jen pro naše měření — proto se zkrátí, ne odmítne.
 */
export function clampAttributionValue(
  value: string | null | undefined,
  max: number,
): string | undefined {
  if (!value) return undefined;
  return value.length > max ? value.slice(0, max) : value;
}

// ============================================================================
// ARRAY UTILITIES
// ============================================================================

export function groupBy<T, K extends string | number>(
  array: T[],
  keyFn: (item: T) => K,
): Record<K, T[]> {
  return array.reduce(
    (acc, item) => {
      const key = keyFn(item);
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    },
    {} as Record<K, T[]>,
  );
}

export function uniqueBy<T>(array: T[], keyFn: (item: T) => unknown): T[] {
  const seen = new Set();
  return array.filter((item) => {
    const key = keyFn(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Split an array into chunks of `size`. The default 80 keeps a chunked `inArray` (one bound
 * parameter per element) safely under D1's 100-bound-parameters-per-query limit with headroom
 * for a few extra WHERE params. Multi-column INSERTs bind size×columns parameters — pass an
 * explicitly smaller size there (e.g. 20 rows × 4 columns = 80).
 */
export function chunk<T>(array: readonly T[], size = 80): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < array.length; i += size) out.push(array.slice(i, i + size));
  return out;
}

/**
 * Redakční pořadí destinací: `sortOrder` vzestupně, při shodě česky podle názvu.
 *
 * JEDNA kopie tohohle komparátoru — do téhle chvíle byl doslova opsaný na PĚTI místech
 * (mega menu na API, výběr destinace ve výpisu 2×, hero hledač 2×, chipy inspirací).
 * Chybějící `sortOrder` je 0, ne konec seznamu: nezařazená destinace patří mezi ty
 * s výslovnou nulou, ne za všechny ostatní.
 *
 * Česká kolace (`compareCzech`) je součást pravidla, ne kosmetika — bez ní by „Čína" skončila
 * až za „Zanzibarem".
 */
export function bySortOrderThenName(
  a: { sortOrder?: number | null; name: string },
  b: { sortOrder?: number | null; name: string },
): number {
  return (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || compareCzech(a.name, b.name);
}

export function sortBy<T>(
  array: T[],
  keyFn: (item: T) => number | string,
  order: "asc" | "desc" = "asc",
): T[] {
  const sorted = [...array].sort((a, b) => {
    const aVal = keyFn(a);
    const bVal = keyFn(b);
    if (aVal < bVal) return -1;
    if (aVal > bVal) return 1;
    return 0;
  });
  return order === "desc" ? sorted.reverse() : sorted;
}

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

/**
 * Kolik hvězdiček se má vybarvit pro dané hodnocení — JEDNA definice pro celý web.
 *
 * ZAOKROUHLUJE, neuřezává. Zadání majitele: „máme 4,9 na Googlu a všude svítí čtyři
 * hvězdičky, mají svítit všechny." `Math.floor` na tom čísle je z pohledu zákazníka
 * lež o desetinu bodu — a je to přesně ta chyba, kterou plodí, když si každé místo
 * pravidlo napíše samo: web měl tři různá (`floor`, `floor` + půlhvězda, `round`),
 * takže tatáž známka vypadala na úvodní stránce jinak než na stránce recenzí.
 *
 * Půlhvězdy VĚDOMĚ nejsou: se zaokrouhlením na celé hvězdy nemají co znamenat a přesná
 * hodnota stojí jako číslo hned vedle hvězdiček všude, kde se hodnocení vykresluje.
 *
 * Nesmysl z API (záporné číslo, `NaN`, hodnota nad stupnicí) padá do rozsahu 0–5,
 * protože hvězdiček je pět a šestá by mřížku rozbila.
 */
export function ratingStars(rating: number | null | undefined): number {
  if (typeof rating !== "number" || !Number.isFinite(rating)) return 0;
  return Math.max(0, Math.min(5, Math.round(rating)));
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export function isValidPhone(phone: string): boolean {
  const cleaned = phone.replace(/[\s\-()]/g, "");
  return /^\+?[0-9]{9,15}$/.test(cleaned);
}

export function sanitizePhone(phone: string): string {
  return phone.replace(/[\s\-()]/g, "");
}

/**
 * Odkaz „ukaž tuhle adresu v mapě". Adresa firmy je editovatelná v adminu (Settings →
 * `company.address`), takže odkaz na mapu se z ní MUSÍ odvodit — natvrdo zapsaná URL se
 * po přestěhování kanceláře tiše rozejde s tím, co je vedle ní napsané.
 */
export function googleMapsSearchUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/** Vložená mapa (iframe) pro tutéž adresu — bez API klíče, jen `output=embed`. */
export function googleMapsEmbedUrl(query: string): string {
  return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=16&output=embed`;
}

/**
 * Normalize a phone number for identity matching (Block 5.5 — call pairing): strips
 * spaces, dashes, dots and parentheses, converts an international `00` prefix to `+`.
 * Returns "" for anything that isn't a plausible phone number (non-digit garbage or
 * fewer than 6 digits) — too short to identify anyone, so callers never match on it.
 */
export function normalizePhone(phone: string | null | undefined): string {
  if (!phone) return "";
  let p = phone.replace(/[\s\-().]/g, "");
  if (p.startsWith("00")) p = `+${p.slice(2)}`;
  if (!/^\+?\d+$/.test(p)) return "";
  return p.replace(/\D/g, "").length >= 6 ? p : "";
}

/**
 * Telefon pro zobrazení (karta garanta, P6/F3.1): česká a slovenská čísla v jednom
 * tvaru `+42x XXX XXX XXX`, aby šla přečíst i vytočit ze zahraničí. Tvoří se při
 * vykreslení — uložené číslo se nepřepisuje. Odkaz `tel:` z výsledku dělá
 * `sanitizePhone`.
 *
 * Normalizaci (oddělovače, `00` → `+`, nečíslo) dělá `normalizePhone`; tady se jen
 * rozhoduje nad jeho výstupem:
 *  - 9 číslic bez předvolby = české národní číslo → `+420 …` (firma i data jsou česká;
 *    slovenské číslo bez předvolby by dostalo `+420` — redakce ho opraví zápisem předvolby),
 *  - `+420` / `+421` a 9 číslic → `+42x …`,
 *  - cokoli jiného (cizí plány číslování, nečíslo, krátké číslo) se vrací jen oříznuté.
 */
export function formatPhoneDisplay(raw: string | null | undefined): string {
  const trimmed = raw?.trim() ?? "";
  if (!trimmed) return "";
  const n = normalizePhone(trimmed);
  const national = /^\d{9}$/.test(n) ? `+420${n}` : n;
  const m = /^\+(42[01])(\d{3})(\d{3})(\d{3})$/.exec(national);
  return m ? `+${m[1]} ${m[2]} ${m[3]} ${m[4]}` : trimmed;
}

/**
 * Conservative phone equality for pairing: exact match after normalization, or a
 * last-9-digits match when BOTH sides have at least 9 digits (handles a missing
 * country code: 777123456 ≈ +420777123456). Short numbers never suffix-match —
 * a false pair attaches a transcript to the wrong person, so when in doubt: no match.
 */
export function phonesMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  const na = normalizePhone(a);
  const nb = normalizePhone(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  const da = na.replace(/\D/g, "");
  const db = nb.replace(/\D/g, "");
  return da.length >= 9 && db.length >= 9 && da.slice(-9) === db.slice(-9);
}

// ============================================================================
// SAFE PARSING UTILITIES
// ============================================================================

/**
 * Safely parse an integer from a string value.
 * Returns the fallback if the value is undefined, null, empty, or NaN.
 */
export function safeParseInt(value: string | undefined | null, fallback: number): number {
  if (value === undefined || value === null || value === "") return fallback;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

/**
 * Safely parse JSON from a string value.
 * Returns the fallback if parsing fails.
 */
export function safeJsonParse<T>(input: string | undefined | null, fallback: T): T {
  if (input === undefined || input === null || input === "") return fallback;
  try {
    return JSON.parse(input) as T;
  } catch {
    return fallback;
  }
}

// ============================================================================
// ID GENERATION
// ============================================================================

export function generateId(prefix: string = ""): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return prefix ? `${prefix}_${timestamp}${random}` : `${timestamp}${random}`;
}

// ============================================================================
// FAQ
// ============================================================================

export interface FaqItem {
  question: string;
  answer: string;
}

/** Tvar z DB: seskupené FAQ (admin `FaqEditor` ukládá skupiny s `topic`). */
export interface FaqTopicGroup {
  topic: string;
  items: FaqItem[];
}

/** Tvar pro `SectionedFAQ` na webu. */
export interface FaqSection {
  title: string;
  items: FaqItem[];
}

/** Titulek jediné sekce, do které se zabalí plochý (nesekcovaný) seznam otázek. */
export const DEFAULT_FAQ_SECTION_TITLE = "Časté dotazy";

/**
 * FAQ sloupce (`trips.faqs`, `destinations.faqs`) nesou HISTORICKY dva tvary: starší
 * plochý seznam `{question, answer}[]` a novější seskupený `{topic, items}[]`. Kód, který
 * je vykresluje, potřebuje jeden — proto se sjednocují tady, ne v každé šabloně zvlášť
 * (dřív tentýž `"topic" in raw[0]` sniff žil doslova v `/hotel`, `/zajezd` i `/destinace`).
 *
 * Prázdný/neplatný vstup vrátí `fallback` (default `[]` = sekci vůbec nevykreslit), takže
 * volající si volí mezi „bez dat nic" a „bez dat obecné otázky CK".
 *
 * `flatTitle` je titulek, pod který se schová plochý (starý) seznam. Stránka, která nad
 * akordeonem už jeden nadpis má — inspirace mají h2 „Užitečné tipy" — si sem posílá
 * neutrální „Obecné", aby týž text nestál dvakrát pod sebou.
 */
export function normalizeFaqSections(
  raw: FaqItem[] | FaqTopicGroup[] | null | undefined,
  fallback: FaqSection[] = [],
  flatTitle: string = DEFAULT_FAQ_SECTION_TITLE,
): FaqSection[] {
  if (!Array.isArray(raw) || raw.length === 0) return fallback;
  const first: unknown = raw[0];
  if (typeof first === "object" && first !== null && "topic" in first) {
    return (raw as FaqTopicGroup[]).map((group) => ({
      title: group.topic,
      items: group.items ?? [],
    }));
  }
  return [{ title: flatTitle, items: raw as FaqItem[] }];
}
